import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    IconButton,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import type { IExternalAccount } from '../../redux/account/types';
import { updateExternalAccount } from '../../redux/account/actions';
import type { RootState } from '../../redux/root-reducer';
import { fetchBankNameFromRouting, isValidAbaRoutingNumber } from '../../utils/routingNumberLookup';
import { hasValidResidentialAddress } from '../../utils/externalAccountPayoutHelpers';
import { showToastMessage } from '../../utils/AppUtils';
import { ResidentialAddressField } from './ResidentialAddressField';

function unmaskAccountNumber(value: string | number | undefined): string {
    if (value == null) return '';
    const s = String(value);
    // Masked values like ******2043 — leave blank so user re-enters full number
    if (s.includes('*')) return '';
    return s.replace(/\D/g, '');
}

interface EditAchAccountModalProps {
    open: boolean;
    account: IExternalAccount | null;
    onClose: () => void;
}

/** Edit ACH routing, account number, and residential address (PATCH /account/external/:id). */
export const EditAchAccountModal: React.FC<EditAchAccountModalProps> = ({ open, account, onClose }) => {
    const dispatch = useDispatch();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const updating = useSelector((state: RootState) => state.account.updatingExternalAccount);
    const updateErr = useSelector((state: RootState) => state.account.updatingExternalAccountErr);

    const [routingNumber, setRoutingNumber] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [bankAddress, setBankAddress] = useState('');
    const [bankName, setBankName] = useState<string | null>(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const lookupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!open || !account) return;
        const rn = String(account.bank_details?.routing_number ?? '').replace(/\D/g, '');
        setRoutingNumber(rn.slice(0, 9));
        setAccountNumber(unmaskAccountNumber(account.bank_details?.account_number));
        setBankAddress(String(account.bank_details?.address ?? '').trim());
        setBankName(account.bank_details?.bank_name?.trim() || null);
        setSubmitted(false);
    }, [open, account]);

    useEffect(() => {
        if (!submitted || updating) return;
        setSubmitted(false);
        if (!updateErr) onClose();
    }, [submitted, updating, updateErr, onClose]);

    useEffect(() => {
        if (!open) return;
        if (routingNumber.length !== 9 || !isValidAbaRoutingNumber(routingNumber)) {
            setLookupLoading(false);
            return;
        }
        if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);
        setLookupLoading(true);
        lookupTimerRef.current = setTimeout(() => {
            fetchBankNameFromRouting(routingNumber)
                .then(name => setBankName(name))
                .catch(() => setBankName(null))
                .finally(() => setLookupLoading(false));
        }, 400);
        return () => {
            if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);
        };
    }, [routingNumber, open]);

    const formValid = useMemo(() => {
        return (
            isValidAbaRoutingNumber(routingNumber) &&
            accountNumber.length >= 4 &&
            /^\d+$/.test(accountNumber) &&
            hasValidResidentialAddress(bankAddress)
        );
    }, [routingNumber, accountNumber, bankAddress]);

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: t.form_divider },
            '&:hover fieldset': { borderColor: t.highlight },
            '&.Mui-focused fieldset': { borderColor: t.highlight },
            backgroundColor: t.form_fields,
            color: t.tag_white,
        },
        '& .MuiInputLabel-root': { color: t.form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM },
        '& .MuiInputBase-input': { fontFamily: FONT_ENUM.MAIN_REGULAR, color: t.tag_white },
        mb: 1.5,
    };

    const handleSave = () => {
        const id = String(account?._id ?? account?.id ?? '');
        if (!id || !formValid) {
            showToastMessage('Enter a valid routing number, account number, and residential address.', 'error');
            return;
        }
        dispatch(
            updateExternalAccount({
                id,
                payload: {
                    name: bankName || account?.name || 'ACH Transfer',
                    bank_details: {
                        routing_number: routingNumber,
                        account_number: accountNumber,
                        address: bankAddress.trim(),
                        ...(bankName ? { bank_name: bankName } : {}),
                    },
                },
            }),
        );
        setSubmitted(true);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    bgcolor: t.deep_grey_lighter,
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.08)',
                },
            }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: t.tag_white,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                }}>
                Edit ACH account
                <IconButton onClick={onClose} size="small" sx={{ color: t.tag_white }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ color: t.light_gray_text, fontSize: 13, mb: 2, fontFamily: FONT_ENUM.MAIN_REGULAR }}>
                    Update the routing number, account number, and residential address for this ACH payout account.
                </Typography>
                <TextField
                    label="Routing Number"
                    fullWidth
                    value={routingNumber}
                    onChange={e => setRoutingNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
                    inputProps={{ inputMode: 'numeric', maxLength: 9 }}
                    error={routingNumber.length === 9 && !isValidAbaRoutingNumber(routingNumber)}
                    helperText={
                        routingNumber.length === 9 && !isValidAbaRoutingNumber(routingNumber)
                            ? 'Routing number is not valid (check digit)'
                            : undefined
                    }
                    sx={fieldSx}
                />
                {(lookupLoading || bankName) && (
                    <Typography variant="caption" sx={{ display: 'block', mb: 1.5, ml: 0.5, color: t.light_gray_text }}>
                        {lookupLoading ? 'Looking up bank…' : bankName ? `Bank: ${bankName}` : null}
                    </Typography>
                )}
                <TextField
                    label="Account Number"
                    fullWidth
                    value={accountNumber}
                    onChange={e => setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 17))}
                    inputProps={{ inputMode: 'numeric', maxLength: 17 }}
                    placeholder={account?.bank_details?.account_number ? 'Enter full account number' : undefined}
                    helperText={
                        String(account?.bank_details?.account_number ?? '').includes('*')
                            ? 'Re-enter the full account number (masked values cannot be saved).'
                            : undefined
                    }
                    sx={fieldSx}
                />
                <ResidentialAddressField
                    value={bankAddress}
                    onChange={setBankAddress}
                    fieldSx={fieldSx}
                    highlightColor={t.highlight}
                    labelColor={t.form_label}
                    mutedColor={t.light_gray_text}
                    surfaceColor={t.secondaryBg}
                    hoverBgColor={t.button_background}
                    textColor={t.tag_white}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} sx={{ color: t.tag_white, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={!formValid || updating}
                    variant="contained"
                    sx={{
                        bgcolor: t.highlight,
                        color: t.black,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        '&:hover': { bgcolor: t.highlight, opacity: 0.9 },
                    }}>
                    {updating ? <CircularProgress size={20} sx={{ color: t.black }} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditAchAccountModal;
